<template>
	<BaseSearchForm :fetch="searchFormConfig" v-slot="{searchResults, updateValue, totalSearchResults, searchTerm, loading, handleInput, onReset, selectedIndex, onSubmit}" ref="searchForm">
		<Body :class="{'search-active': swOpen}" />
		<div class="sw" :class="[{'loading': loading}, {'active': swOpen}]" ref="sw">
			<div class="sw-toggle" @click="showSearch"></div>
			
			<div class="sw-form" id="main_search" method="get">
				<input ref="searchInput" class="swap-value sw-input" name="search_q" id="search_q" type="text" :value="searchTerm" @input="updateValue" autocomplete="off" @keyup="handleInput" @keyup.enter="onMobileSearch" :placeholder="labels.get('search_term')"/>
				<button @click="onSubmit(), onMobileSearch($event)" class="sw-btn" type="submit"></button>

				<ClientOnly>
					<div class="ac" v-if="totalSearchResults || loading">
						<div class="ac-loading" v-if="loading" />
						<div class="ac-wrapper">
							<div class="ac-col1">
								<div v-if="searchResults?.catalogcategory?.length" class="ac-menu ac-categories">
									<div class="ac-title">{{ labels.get('autocomplete_categories') }}</div>
									<div class="ac-menu-item-cnt" v-for="item in searchResults.catalogcategory" :key="item.id">
										<NuxtLink @click="onReset" :to="item.url_without_domain" class="ac-menu-item" :class="{'active': item.index == selectedIndex}">
											<span v-html="formatText(item.title, searchTerm)"></span>
										</NuxtLink>
									</div>
								</div>
								<div v-if="searchResults?.catalogmanufacturer?.length" class="ac-menu ac-brands">
									<div class="ac-title">{{ labels.get('autocomplete_brands') }}</div>
									<div class="ac-menu-item-cnt" v-for="item in searchResults.catalogmanufacturer" :key="item.id">
										<NuxtLink @click="onReset" :to="item.url_without_domain" class="ac-menu-item" :class="{'active': item.index == selectedIndex}">
											<span v-html="formatText(item.title, searchTerm)"></span>
										</NuxtLink>
									</div>
								</div>
								<div v-if="searchResults?.['publish.02']?.length" class="ac-menu ac-posts">
									<div class="ac-title">{{ labels.get('autocomplete_publish') }}</div>
									<div class="ac-menu-item-cnt" v-for="item in searchResults['publish.02']" :key="item.id">
										<NuxtLink @click="onReset" :to="item.url_without_domain" class="ac-menu-item" :class="{'active': item.index == selectedIndex}">
											<span v-html="formatText(item.title, searchTerm)"></span>
										</NuxtLink>
									</div>
								</div>
							</div>
							<div class="ac-menu ac-products" v-if="searchResults?.catalogproduct?.length">
								<NuxtLink @click="onReset" v-for="item in searchResults.catalogproduct" :key="item.id" :to="item.url_without_domain" class="ac-item" :class="{'active': item.index == selectedIndex}">
									<div class="ac-item-img">
										<BaseUiImage loading="lazy" :src="item.image_upload_path" width="50" height="50" default="/images/no-image-50.jpg" />
									</div>
									<div class="ac-item-cnt">
										<div class="ac-item-title">{{ item.title }}</div>
										<div class="ac-item-code">{{ item.code }}</div>
									</div>
									<div class="ac-item-price">
										<template v-if="(item.discount_percent_custom > 0 || item.price_custom < item.basic_price_custom)">
											<div class="ac-current-price ac-discount-price"><BaseUtilsFormatCurrency :price="item.price_custom" /></div>
										</template>
										<template v-else>
											<div class="ac-current-price"><BaseUtilsFormatCurrency :price="item.price_custom" /></div>
										</template>
									</div>
								</NuxtLink>
								<div class="ac-showall">
									<NuxtLink @click="onReset" class="ac-showall-btn" :to="searchResults?.catalogproduct_show_all.url_without_domain">
										<strong>{{ labels.get('all_products') }} <span>({{ searchResults.catalogproduct_show_all.total }})</span></strong>
									</NuxtLink>
								</div>
							</div>
						</div>
					</div>
				</ClientOnly>
			</div>
		</div>
	</BaseSearchForm>
</template>

<script setup>
	const labels = useLabels();
	const {onClickOutside} = useDom();
	const {mobileBreakpoint} = inject('rwd');

	const searchFormConfig = {
		'allow_models': ['catalogcategory', 'catalogmanufacturer', 'catalogproduct', 'publish', 'publish.02'],
		'result_fields': {
			'catalogproduct': ['image', 'price', 'code', 'price_custom', 'basic_price', 'basic_price_custom', 'discount_percent', 'discount_percent_custom'],
		},
		'result_per_page': {
			'_default': 5
		},
		'result_image': '50x50_r'
	}

	const sw = ref(null);
	const swOpen = ref(false);
	const searchForm = ref(null);
	onClickOutside(sw, () => {
		searchForm.value.resetAutocomplete();
		if(!mobileBreakpoint.value){
			swOpen.value = false;
		}
	});

	let windowOffset = 0;
	function showSearch() {
		if (swOpen.value == false) {
			windowOffset = window.scrollY;
			swOpen.value = true;
		} else {
			swOpen.value = false;
			searchForm.value.resetAutocomplete();
			setTimeout(function(){
				window.scroll(0,windowOffset);
			},50);
		}
	}

	function onMobileSearch(e) {
		if(mobileBreakpoint.value) swOpen.value = false;
	}

	function formatText(title, term){
		if (!title || !term) return title;
		const regex = new RegExp(`(${term})`, 'gi');
		return title.replace(regex, '<strong>$1</strong>');
	}
</script>

<style lang="less" scoped>
	.sw{
		width: 270px; position: relative;
		@media (max-width: @l){width: 230px;}
		@media (max-width: @tp){
			width: 22px; height: 56px; position: absolute; right: 148px;
			&.active{
				width: auto; height: auto; right: 0; left: 0;
				.sw-form{
					display: block; width: auto; height: auto; left: 0; right: 0; z-index: 9999;
					input{background: white; width: 100%; padding: 0 56px; color: var(--textColor); height: 56px; z-index: 1001;}
				}
				.sw-toggle{
					right: unset; left: 0; background: none; right: 0; left: 0; z-index: 99999; height: 56px; width: 56px; position: absolute;
					&:before{.icon-close(); width: 56px; height: 56px; color: var(--primaryColor); font: 14px/14px var(--fonti);}
				}
			}
		}
	}
	.sw-toggle{
		right: 0; transition: unset; width: 50px; height: 50px; transition: var(--transition); cursor: pointer; position: relative;
		&:before{.pseudo(50px,50px); font: 19px/19px var(--fonti); .icon-search; color: white; display: flex; align-items: center; justify-content: center;}
		@media(min-width: @h){
			&:hover{background: #472266;}
		}
		@media (max-width: @l){width: 44px; height: 44px;}
		@media (max-width: @tp){
			width: 22px; height: 56px; display: block;
			&:before{width: 22px; height: 56px; font: 22px/22px var(--fonti);}
		}
	}
	.sw-form{
		position: absolute; right: 0; top: 0; z-index: 99;
		input{
			width: 270px; background: #472266; height: 50px; font-size: 16px; color: white; padding: 0 50px 0 20px; border: 0; z-index: 1000; position: relative; border-radius: 0;
			@media (max-width: @l){width: 230px; height: 44px; font-size: 14px;}
			@media (max-width: @tp){font-size: 16px;}
		}
		@media (max-width: @tp){display: none;}
	}
	.sw-btn{
		margin: 0; width: 50px; min-width: 50px; background: #472266; height: 50px; top: 0; position: absolute; right: 0; padding: 0; border-radius: 0; z-index: 1001;
		&:before{.icon-search(); color: white; width: 50px; height: 50px; font: 19px/19px var(--fonti); display: flex; align-items: center; justify-content: center;}
		@media (max-width: @l){
			width: 44px; height: 44px; min-width: 44px;
			&:before{width: 44px; height: 44px; font: 17px/17px var(--fonti);}
		}
		@media (max-width: @tp){
			width: 56px; height: 56px; min-width: 56px;
			&:before{width: 56px; height: 56px;}
		}
	}

	
	//Autocomplete
	.ac{
		position: absolute; top: 50px; right: -100px; left: -265px; background: var(--white); height: auto; z-index: 10001; box-shadow: 0 0px 40px 0 rgba(0,0,0,0.1); border-radius: 0 0 2px 2px;
		@media (max-width: @l){top: 44px; right: -88px;}
		@media (max-width: @t){left: -250px;}
		@media (max-width: @tp){left: 0; right: 0; top: 56px; border-top: 1px solid var(--borderColor);}
	}
	.ac-wrapper{
		position: relative; display: flex; /* max-height: 755px; overflow-y: auto; margin-right: 2px; */
		&:empty{padding: 0; box-shadow: none;}
		/* &::-webkit-scrollbar{-webkit-appearance: none; width: 6px; background: @graySeparators; z-index: 0; border-radius: 2px;}
		&::-webkit-scrollbar-thumb{background-color: @orange; border-radius: 2px;} */
		@media (max-width: @m){flex-flow: column;}
	}
	.ac-col1{
		width: 265px; display: block; padding: 15px 20px 20px; background: var(--white); border-right: 1px solid var(--borderColor); font-weight: 300;
		@media (max-width: @t){width: 252px; padding: 12px 20px 20px;}
		@media (max-width: @tp){width: 220px; padding: 12px 18px 18px;}
		@media (max-width: @m){padding: 10px 12px 15px; border-right: 0; width: 100%; order: 2;}
	}
	.ac-products{
		flex-grow: 1; position: relative; width: auto; flex-shrink: 0;
		&:before{
			.pseudo(8px,8px); left: 22px; top: -4px; background: var(--white); transform: rotate(45deg); z-index: 9998;
			@media (max-width: @tp){display: none;}
		}
		@media (max-width: @m){order: 1; padding-bottom: 0!important;}
	}

	.ac-title{
		font-size: 16px; line-height: 22px; color: var(--textColor); font-weight: bold; padding-bottom: 3px;
		@media (max-width: @t){font-size: 16px; line-height: 20px; padding-bottom: 2px;}
		@media (max-width: @m){font-size: 16px; line-height: 21px; padding-bottom: 2px;}
	}
	.ac-menu{
		padding-bottom: 15px;
		@media (max-width: @t){padding-bottom: 12px;}
	}
	.ac-menu-item-cnt{display: block;}
	.ac-menu-item{
		display: inline-block; font-size: 16px; line-height: 22px; padding: 2px 0; color: var(--textColor); text-decoration-color: transparent; .transition(text-decoration-color);
		@media (min-width: @h){
			&:hover{text-decoration-color: var(--textColor);}
		}
		@media (max-width: @t){font-size: 14px; line-height: 18px;}
		@media (max-width: @tp){font-size: 14px; line-height: 20px;}
		@media (max-width: @m){font-size: 13px; line-height: 18px;}
	}
	
	//products
	.ac-item{
		position: relative; display: flex; color: var(--textColor); border-bottom: 1px solid var(--borderColor); text-decoration-color: transparent; line-height: 17px; padding: 12px 14px;
		@media (min-width: @h){
			&:hover{
				.ac-item-title{color: var(--primaryColor);}
			}
		}
		@media (max-width: @t){padding: 8px 10px;}
		@media (max-width: @tp){padding: 7px 9px;}
	}
	.ac-item-img{
		display: flex; align-items: center; justify-content: center; width: 80px; height: 70px; flex-shrink: 0;
		:deep(img){display: block; width: auto; height: auto; max-width: 100%; max-height: 100%;}
		@media (max-width: @t){width: 70px; height: 60px; min-width: 70px; min-height: 60px;}
		@media (max-width: @tp){width: 60px; height: 50px; min-width: 60px; min-height: 50px;}
	}
	.ac-item-cnt{
		flex-grow: 1; padding-left: 12px; padding-right: 85px;
		@media (max-width: @t){padding-left: 10px; padding-right: 65px;}
		@media (max-width: @tp){padding-left: 8px; padding-right: 63px;}
	}
	.ac-item-title{
		display: block; font-size: 16px; line-height: 20px; padding: 0 0 1px 0; text-decoration: none; transition: color 0.3s; max-width: 180px; font-weight: 300;
		@media (max-width: @t){padding: 0 0 1px; font-size: 14px; line-height: 18px; max-width: 140px;}
		@media (max-width: @m){font-size: 12px; line-height: 16px; max-width: unset;}
	}
	.ac-item-code{
		font-size: 13px; line-height: 17px; color: rgba(43, 44, 55, 0.5); font-weight: normal;
		@media (max-width: @t){font-size: 11px; line-height: 15px;}
	}
	
	.ac-item-price{
		position: absolute; top: 12px; right: 15px; display: flex; flex-flow: column; text-align: right;
		@media (max-width: @t){top: 13px; right: 10px;}
	}
	.ac-current-price{
		font-size: 16px; line-height: 20px; display: flex; flex-flow: column;
		@media (max-width: @t){font-size: 14px; line-height: 18px;}
	}
	.ac-discount-price{font-weight: bold; color: var(--primaryColor);}

	//products button
	.ac-showall{
		text-align: center; padding: 15px; position: relative; display: block;
		@media (max-width: @t){padding: 10px;}
	}
	.ac-showall-btn{
		position: relative; color: var(--white); text-decoration: none; width: 100%; font-weight: inherit; min-height: 60px; height: 60px; font-size: 18px; line-height: 17px; margin: 0; display: inline-flex; align-items: center; justify-content: center; padding: 0; text-align: center; .transition(all);
		strong{font-weight: 300; font-size: 18px; line-height: 24px; z-index: 999; position: relative;}
		&:before{background: var(--primaryColorGrad); .pseudo(auto,auto); top: 0; left: 0; right: 0; bottom: 0; border-radius: var(--borderRadius); z-index: 9; .transition(all);}
		@media(min-width: @h){
			&:hover{
				&:before{background: var(--primaryColor) !important;}
			}
		}
		@media (max-width: @t){
			min-height: 50px; height: 50px; font-size: 14px; line-height: 20px;
			strong{font-size: 14px; line-height: 20px;}
		}
		@media (max-width: @m){min-height: 44px; height: 44px; font-size: 14px; line-height: 20px;}
	}
</style>